import { useEffect, useMemo, useState, type ComponentProps } from "react";
import { Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { selectCurrentUserId } from "@/features/auth/currentUser";
import { useChatThreads, type ChatThread } from "@/features/chat/api/useChatThreads";
import { subscribeOpenChat } from "@/features/chat/utils/chatOpenBus";
import { useInternalChat, type ChatMessage } from "@/features/leads/api/useInternalChat";
import { useInternalChatMutations } from "@/features/leads/api/useInternalChatMutations";
import { ChatMessageList } from "@/features/leads/components/view-lead/components/Chat/components/ChatMessageList";
import ActionForChat from "@/features/leads/components/view-lead/components/Actions/ActionForChat";
import { useAppSelector } from "@/stores/hook";
import { openLead } from "../../lead/openLead";
import { M } from "../../theme";
import Avatar from "../../ui/Avatar";
import FullScreen from "../../ui/FullScreen";
import { closeThread, openThread, useOpenThread } from "./threadStore";

type SendArgs = Parameters<ComponentProps<typeof ActionForChat>["onSend"]>;

/**
 * The full-screen internal chat thread (the app's Conversation, internal
 * skin): a white header with the lead's name and the Lead / per-deal channel
 * chips, the existing message list and composer in between. Mounted by the
 * layout: it also listens to `requestOpenChat`, the bus every chat toast and
 * @-mention popup already talks to, and takes the Chat tab there.
 */
export default function InternalThreadHost() {
  const open = useOpenThread();
  const navigate = useNavigate();
  useEffect(
    () =>
      subscribeOpenChat((req) => {
        openThread({ leadId: req.leadId, leadName: req.leadName ?? null, childId: req.childId ?? null, messageId: req.messageId ?? null });
        navigate("/chat?seg=internal");
      }),
    [navigate],
  );
  if (!open) return null;
  return <InternalThread key={`${open.leadId}:${open.childId ?? ""}`} thread={open} />;
}

function InternalThread({ thread }: { thread: { leadId: number; leadName?: string | null; childId?: number | null; messageId?: string | null } }) {
  const userId = useAppSelector(selectCurrentUserId) ?? undefined;
  const { threads } = useChatThreads();
  const meta: ChatThread | undefined = useMemo(() => threads.find((t) => t.leadId === thread.leadId), [threads, thread.leadId]);
  const [childId, setChildId] = useState<number | null>(thread.childId ?? null);
  const { messages, isLoading, hasMore, fetchOlder, ensureMessageLoaded } = useInternalChat(thread.leadId, childId);
  const { sendMessage, uploadFile, markAsRead, deleteMessage, editMessage } = useInternalChatMutations();
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [editing, setEditing] = useState<ChatMessage | null>(null);

  // Everything unread in view is read now.
  useEffect(() => {
    const unread = messages.filter((m) => m.senderId !== userId && !m.readBy?.some((r) => r.userId === userId)).map((m) => m.id);
    if (unread.length) markAsRead.mutate({ leadId: thread.leadId, childId, messageIds: unread });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, childId]);
  useEffect(() => {
    if (thread.messageId) void ensureMessageLoaded?.(thread.messageId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread.messageId]);

  const name = thread.leadName || meta?.leadName || `Lead ${thread.leadId}`;
  const subtitle = [meta?.stageName, meta?.statusName].filter(Boolean).join(" · ");
  const channels = meta?.children ?? [];

  const send = (...[text, mentions]: SendArgs) => {
    sendMessage.mutate({ leadId: thread.leadId, childId, body: text, type: "text", mentions, replyToId: replyTo?.id });
    setReplyTo(null);
  };

  return (
    <FullScreen
      open
      onClose={closeThread}
      bg={M.bg}
      header={
        <button type="button" onClick={() => openLead(thread.leadId, childId ?? undefined)} className="flex min-w-0 flex-1 items-center gap-2.5 text-left">
          <Avatar name={name} size={34} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[15px] font-semibold tracking-[-0.3px]" style={{ color: M.text }}>
              {name}
            </span>
            <span className="block truncate text-[10.5px]" style={{ color: M.textMuted }}>
              {subtitle || "Internal chat"}
            </span>
          </span>
          <Info size={18} color={M.textFaint} />
        </button>
      }
    >
      <div className="flex h-full flex-col">
        {channels.length > 0 ? (
          <div className="xm-noscroll flex shrink-0 gap-1.5 overflow-x-auto bg-white px-3.5 py-2" style={{ borderBottom: `1px solid ${M.hairline}` }}>
            <ChannelChip label="Lead" on={childId == null} onClick={() => setChildId(null)} />
            {channels.map((c) => (
              <ChannelChip key={c.id} label={c.name} on={childId === c.id} onClick={() => setChildId(c.id)} />
            ))}
          </div>
        ) : null}
        <div className="min-h-0 flex-1 overflow-y-auto px-2">
          {hasMore ? (
            <button type="button" onClick={() => void fetchOlder()} className="block w-full py-2 text-center text-[11.5px] font-semibold" style={{ color: M.accent }}>
              Load earlier messages
            </button>
          ) : null}
          <ChatMessageList
            messages={messages}
            currentUserId={userId}
            isLoading={isLoading}
            onReply={(m) => setReplyTo(m)}
            onEdit={(m) => setEditing(m)}
            onDelete={(m) => deleteMessage.mutate({ messageId: m.id })}
          />
        </div>
        <div className="shrink-0 bg-white px-2 pt-1" style={{ borderTop: `1px solid ${M.hairline}`, paddingBottom: "calc(env(safe-area-inset-bottom) + 6px)" }}>
          <ActionForChat
            onSend={send}
            onFileUpload={(file) => uploadFile.mutate({ leadId: thread.leadId, childId, file })}
            onVoiceNote={(blob) => uploadFile.mutate({ leadId: thread.leadId, childId, file: blob })}
            isSending={sendMessage.isPending || uploadFile.isPending}
            replyTo={replyTo}
            onCancelReply={() => setReplyTo(null)}
            editing={editing}
            onCancelEdit={() => setEditing(null)}
            onSaveEdit={(messageId, text, mentions) => {
              editMessage.mutate({ messageId, body: text, mentions } as Parameters<typeof editMessage.mutate>[0]);
              setEditing(null);
            }}
            leadUsers={meta?.leadUsers}
          />
        </div>
      </div>
    </FullScreen>
  );
}

function ChannelChip({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className="xm-chip h-7 rounded-full px-3 text-[11.5px] font-medium"
      style={{ backgroundColor: on ? M.greenDeep : M.surfaceAlt, color: on ? "#fff" : M.textMuted }}
    >
      {label}
    </button>
  );
}
